"use client";

import * as React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "../../../lib/utils";
import { kanbanCardVariants } from "./constants";
import { KanbanCardProvider, useKanbanColumn, useKanbanBoard } from "./context";
import type { KanbanCardProps } from "./types";

const KanbanCard = React.forwardRef<HTMLDivElement, KanbanCardProps>(
  ({ className, card, onClick, children, ...props }, ref) => {
    const { column } = useKanbanColumn();
    const { onCardClick, activeDrag, dropIndicator } = useKanbanBoard();

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging: isSortableDragging,
    } = useSortable({
      id: card.id,
      data: { type: "card", card, columnId: column.id },
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const isDragging =
      isSortableDragging ||
      (activeDrag?.type === "card" && activeDrag.id === card.id);

    const showBeforeIndicator =
      dropIndicator.overCardId === card.id &&
      dropIndicator.position === "before" &&
      activeDrag?.id !== card.id;

    const showAfterIndicator =
      dropIndicator.overCardId === card.id &&
      dropIndicator.position === "after" &&
      activeDrag?.id !== card.id;

    const handleClick = (e: React.MouseEvent) => {
      // React's synthetic events bubble through the React tree, not the DOM
      // tree — so clicks inside portaled children (e.g. the card's dropdown
      // menu) reach this handler. Ignore those: their DOM target is outside
      // the card.
      if (!e.currentTarget.contains(e.target as Node)) return;
      e.stopPropagation();
      if (isDragging) return;
      if (onClick) onClick();
      else onCardClick?.(card);
    };

    const cardCtx = React.useMemo(
      () => ({ card, columnId: column.id, isDragging }),
      [card, column.id, isDragging],
    );

    /* Consumer props are spread last so callers can override presentation —
     * but that also let a caller silently clobber dnd-kit's drag activator.
     * `<ContextMenuTrigger asChild>` (which wraps every task card) passes its
     * own `onPointerDown`; spread after `listeners`, it replaced the sensor's
     * and killed dragging with a mouse — Radix guards its handler with
     * `whenTouchOrPen`, so on mouse it ran, did nothing, and the drag never
     * started. Compose the collisions instead: the sensor handler runs first,
     * then the caller's. */
    const composedListeners = React.useMemo(() => {
      if (!listeners) return undefined;
      const merged: Record<string, unknown> = { ...listeners };
      for (const [key, own] of Object.entries(listeners)) {
        const incoming = (props as Record<string, unknown>)[key];
        if (typeof own === "function" && typeof incoming === "function") {
          merged[key] = (event: unknown) => {
            (own as (e: unknown) => void)(event);
            (incoming as (e: unknown) => void)(event);
          };
        }
      }
      return merged;
    }, [listeners, props]);

    return (
      <KanbanCardProvider value={cardCtx}>
        {showBeforeIndicator && (
          <div className="mx-1 -mt-1 mb-1 h-0.5 rounded-full bg-accent" />
        )}

        <div
          ref={(node) => {
            setNodeRef(node);
            if (typeof ref === "function") ref(node);
            else if (ref) ref.current = node;
          }}
          style={style}
          className={cn(
            kanbanCardVariants({ state: isDragging ? "dragging" : "default" }),
            className,
          )}
          {...attributes}
          {...props}
          {...composedListeners}
          onClick={handleClick}
        >
          {children}
        </div>

        {showAfterIndicator && (
          <div className="mx-1 -mb-1 mt-1 h-0.5 rounded-full bg-accent" />
        )}
      </KanbanCardProvider>
    );
  },
);

KanbanCard.displayName = "KanbanCard";

export { KanbanCard };
